import { useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogDescription, DialogActions } from '../catalyst/dialog';
import { Button } from '../catalyst/button';
import { ServerIcon } from '@heroicons/react/24/solid';
import axios from 'axios';

export default function ReloadWarningPopup() {
  const [open, setOpen] = useState(false);
  const [timer, setTimer] = useState(60);
  const [isLoading, setIsLoading] = useState(false);
  const buildNumberRef = useRef<string | null>(null);
  const buildSourceRef = useRef<string | null>(null);
  const buildNumberWantedRef = useRef<string | null>(null);
  const boundRef = useRef(false);

  useEffect(() => {
    if (boundRef.current) {
      return;
    }
    boundRef.current = true;

    console.log('bound');
    axios.interceptors.response.use((response) => {
      // Prefer the frontend bundle fingerprint so backend-only deploys don't
      // prompt a reload; x-build-number covers servers that predate it.
      const frontendBuildNumber = response.headers['x-frontend-build-number'];
      const buildNumber = frontendBuildNumber ?? response.headers['x-build-number'];
      const buildSource = frontendBuildNumber ? 'frontend' : 'deploy';
      const buildDate = new Date(response.headers['x-build-date']);

      if (!buildNumber) {
        return response;
      }

      // const buildId = response.headers["x-build-id"];
      // const environment = response.headers["x-environment"];
      const buildDateDiffSeconds = (new Date().getTime() - buildDate.getTime()) / 1000;
      const lastBuildReloadSeconds =
        (new Date().getTime() - new Date(localStorage.getItem('lastBuildReload') ?? 0).getTime()) / 1000;

      (() => {
        if (!buildNumberRef.current) {
          buildNumberRef.current = buildNumber;
          buildSourceRef.current = buildSource;
        }

        // A rolling deploy mixes servers with and without the frontend header;
        // the two values aren't comparable, so rebaseline instead of prompting.
        if (buildSourceRef.current !== buildSource) {
          buildNumberRef.current = buildNumber;
          buildSourceRef.current = buildSource;
          return;
        }

        if (buildNumberRef.current === buildNumber) {
          return;
        }

        buildNumberWantedRef.current = buildNumber;

        console.warn(`build number does not match ${buildNumberRef.current} !== ${buildNumber}`);

        if (buildDateDiffSeconds < 60) {
          return console.warn(`cooldown after startup ${buildDateDiffSeconds}s...`);
        }

        if (lastBuildReloadSeconds < 60) {
          return console.warn(`refresh is prevented by refresh cooldown ${lastBuildReloadSeconds}s...`);
        }

        localStorage.setItem('lastBuildReload', new Date().toISOString());
        console.log('Showing reload warning popup');
        setOpen(true);
      })();

      return response;
    });
  }, [open]);

  useEffect(() => {
    if (!open) {
      setTimer(60);
      return;
    }
    const interval = setInterval(() => {
      if (localStorage.ignoreBuild === buildNumberWantedRef.current) {
        setOpen(false);
        buildNumberRef.current = buildNumberWantedRef.current;
        return;
      }

      setTimer((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  useEffect(() => {
    if (timer === 0) {
      handleReload();
    }
  }, [timer]);

  const handleReload = () => {
    setIsLoading(true);
    window.location.reload();
  };

  const handleCancel = () => {
    localStorage.ignoreBuild = buildNumberWantedRef.current;
    buildNumberRef.current = buildNumberWantedRef.current;
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={() => void 0} size="sm">
      <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
        <ServerIcon aria-hidden="true" className="size-6 text-amber-600 dark:text-amber-400" />
      </div>
      <DialogTitle className="mt-3 text-center">Reload Warning</DialogTitle>
      <DialogDescription className="text-center">
        A new version of the dashboard is available. To access the latest features and improvements, please reload the
        page. Would you like to refresh now or cancel and continue with the current version?
      </DialogDescription>
      <DialogActions>
        <Button plain onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button color="indigo" onClick={handleReload} disabled={isLoading}>
          Reload now ({timer}s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
